import {
  Component,
  Input,
  ContentChild,
  AfterContentInit,
  ElementRef,
  forwardRef,
  Output,
  EventEmitter,
  input,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common'; // Import CommonModule
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { RequiredPermission} from '../../../lib/models/settlement-permissions.model';
import { HasPermissionDirective } from '../../../lib/directives/has-permission.directive';

type InputSize = 'sm' | 'lg';
type InputVariant = 'filled' | 'outlined';

@Component({
  selector: 'app-text-input',
  standalone: true, // Ensure standalone is true
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, HasPermissionDirective], // Add CommonModule and FormsModule here
  templateUrl: './text-input.component.html',
  styleUrl: './text-input.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextInputComponent),
      multi: true,
    },
  ],
})
export class TextInputComponent implements AfterContentInit, ControlValueAccessor {
  @Input() inputPermission: RequiredPermission | null = null;
  @Input() helperLinkPermission: RequiredPermission | null = null;

  @Input() showCopyIcon = false;
  @Input() type = 'text';
  @Input() ariaLabel: string | null = null;
  @Input() label: string | undefined;
  @Input() placeholder = 'Enter amount';
  @Input() isInvalid = false;
  @Input() errorMessage: string | undefined;
  @Input() precision: number | null = null;
  @Input() maxLength: number | null = null;
  @Input() max: number | null = null;
  @Input() min: number | null = null;
  @Input() disable = false;
  @Input() autoWidth = false;
  @Input() readOnly = false;
  @Input() showRestField = false;
  @Output() restField = new EventEmitter<void>();

  tooltip = viewChild<MatTooltip>('tooltip');
  inputElement = viewChild<ElementRef<HTMLInputElement>>('inputElement');

  icon = input<{name: string; tooltip?: string} | null>(null);
  helperLink = input<string | null>(null);
  inputSize = input<InputSize>('lg');
  variant = input<InputVariant>('filled');
  iconClick = output<void>();
  helperLinkClick = output<void>();

  @ContentChild('[icon]') iconContent: any;
  hasIconContent = false;

  // Value accessor properties
  value = '';
  disabled = false;
  onChange: any = () => {};
  onTouched: any = () => {};

  tooltipText = signal('Copy');
  private _timeout!: ReturnType<typeof setTimeout>;

  ngAfterContentInit() {
    this.hasIconContent = !!this.iconContent;
    this.disabled = this.disable;
  }
  onCopyIconClick() {
    if (this.value === '' || this.value == null) return;

    navigator.clipboard.writeText(this.value).then(() => {
      this.tooltipText.set('Copied');
      this.tooltip()?.show();
    });

    clearTimeout(this._timeout);
    this._timeout = setTimeout(() => {
      this.tooltipText.set('Copy');
    }, 2000);
  }
  // ControlValueAccessor interface methods
  writeValue(value: any): void {
    // Nullish check, not falsy — a numeric 0 must still render.
    this.value = value ?? '';
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Handle input changes
  onInputChange(newValue: any): void {
    // pasted values bypass the keydown guards, so re-apply them here;
    // sanitize before length-limiting so maxLength counts cleaned digits only
    this.value =
      this.type === 'number' && newValue != null && newValue !== ''
        ? this.sanitizeNumberValue(String(newValue))
        : this.limitInputLength(newValue);

    if (this.type === 'number') {
      this.showSanitizedValue();
    }

    // A lone "." is not a number yet: keep showing it, but give the form an empty value so required applies.
    this.onChange(this.type === 'number' && this.value === '.' ? '' : this.value);
  }

  preventInvalidNumberInput(event: KeyboardEvent): void {
    if (this.type === 'number') {
      if (event.ctrlKey || event.metaKey) return;

      const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', '.'];

      const isNumberKey = /^\d$/.test(event.key);
      const isAllowedKey = allowedKeys.includes(event.key);

      if (!isNumberKey && !isAllowedKey) {
        event.preventDefault();
        return;
      }

      const input = event.target as HTMLInputElement;

      if (event.key === '.' && (this.precision === 0 || input.value.includes('.'))) {
        event.preventDefault();
        return;
      }

      if (this.maxLength !== null && (isNumberKey || event.key === '.')) {
        const nextValue = this.getNextInputValue(input, event.key);

        if (nextValue.length > this.maxLength) {
          event.preventDefault();
          return;
        }
      }

      if (this.max !== null && isNumberKey) {
        const nextValue = this.getNextInputValue(input, event.key);
        const nextNumber = Number(nextValue);

        if (Number.isFinite(nextNumber) && nextNumber > this.max) {
          event.preventDefault();
          return;
        }
      }

      if (this.precision !== null && input.value.includes('.') && isNumberKey) {
        const [_, decimalPart = ''] = input.value.split('.');
        const cursorPos = input.selectionStart ?? input.value.length;

        if (cursorPos > input.value.indexOf('.') && decimalPart.length >= this.precision) {
          event.preventDefault();
        }
      }
    }
  }

  onRestField(): void {
    this.value = '';
    this.onChange(this.value);
    this.restField.emit();
  }

  // ngModel skips the DOM write when the cleaned value equals the previous one, which would leave rejected text on screen.
  private showSanitizedValue(): void {
    const input = this.inputElement()?.nativeElement;
    const text = this.value ?? '';

    if (input && input.value !== text) {
      input.value = text;
    }
  }

  private limitInputLength(value: any): any {
    if (this.maxLength === null || value == null) {
      return value;
    }

    const stringValue = String(value);
    return stringValue.length > this.maxLength ? stringValue.slice(0, this.maxLength) : value;
  }

  // Accepts characters one by one under the same rules as preventInvalidNumberInput
  private sanitizeNumberValue(value: string): string {
    let result = '';

    for (const char of value) {
      const isDigit = /^\d$/.test(char);

      if (!isDigit && char !== '.') continue;
      if (char === '.' && (this.precision === 0 || result.includes('.'))) continue;
      if (this.maxLength !== null && result.length >= this.maxLength) continue;
      if (isDigit && this.max !== null && Number(result + char) > this.max) continue;

      if (isDigit && this.precision !== null && result.includes('.')) {
        const decimalPart = result.split('.')[1] ?? '';
        if (decimalPart.length >= this.precision) continue;
      }

      result += char;
    }

    return result;
  }

  private getNextInputValue(input: HTMLInputElement, key: string): string {
    const selectionStart = input.selectionStart ?? input.value.length;
    const selectionEnd = input.selectionEnd ?? input.value.length;
    return input.value.slice(0, selectionStart) + key + input.value.slice(selectionEnd);
  }

  ngOnDestroy() {
    clearTimeout(this._timeout);
  }

  get sizeClass(): string {
    return `size-${this.inputSize()}`;
  }
}
